import styled from '@emotion/styled';
import { Typography, Stack } from '@mui/material';
import { FocusScheduling, FocusVersionScheduling } from '@/redux/slices/slice_types';
import AvailsEnumMap from '@/components/Focus/Avails/AvailsEnumMap';
import { LicenseChip } from '@/components/_common/Table/Calendar/CalendarComponents/common';
import VersionSchedulingAvailChip from '@/components/Focus/AvailsSchedule/VersionSchedulingAvailChip';
import PlatformsList from '@/components/_common/Table/Calendar/CalendarSharedComponents/PlatformsList';
import AvailScheduleOverflowActions from './AvailSchedulOverflowActions';
import AvailScheduleStatus from './AvailScheduleStatus';
import SchedulingTermDateDisplay from './SchedulingTermDateDisplay';
import AvailScheduleRowExpand from './AvailScheduleRowExpand/AvailScheduleRowExpand';
import VersionChip from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/VersionChip';
import TitleHierarchy from '@/components/_common/TitleHierarchy';

export default {
  expandable: {
    label: '',
    isExpandable: true,
    render: (props: { item: FocusScheduling; isCollapsed: boolean }) => <AvailScheduleRowExpand {...props} />
  },
  version__alt_external_id: {
    label: 'Alpha ID',
    minWidth: 150,
    sortField: 'version__alt_external_id',
    render: (cell: FocusVersionScheduling['version']['alt_external_id']) => (
      <AlphaId>
        <Typography fontSize={14}>{cell}</Typography>
      </AlphaId>
    )
  },
  version__parent_name: {
    label: 'Title',
    minWidth: 250,
    disableSort: true,
    render: (cell: FocusVersionScheduling['version']['parent_name'], row: FocusVersionScheduling) => (
      <TitleHierarchy title={{ ...row.version, name: null }} nameFallback={cell} />
    )
  },
  version__name: {
    label: 'Version',
    sortField: 'version__name',
    minWidth: 120,
    render: (cell: FocusVersionScheduling['version']['name']) => <VersionChip versionName={cell} />
  },
  license: {
    label: 'License',
    sortField: 'license',
    minWidth: 120,
    render: (cell: FocusVersionScheduling['license']) => <LicenseChip label={<AvailsEnumMap type="license" value={cell} />} />
  },
  platform: {
    label: 'Platform',
    disableSort: true,
    minWidth: 120,
    render: (cell: FocusVersionScheduling['platform']) => (
      // Right now there is only one platform per schedule, but I use <PlatformsList /> cause it might change in the future
      <PlatformsList platforms={[cell]} limit={3} />
    )
  },
  // NATALIA: Need availability term to return from API
  availability_term: {
    label: 'Availability term',
    minWidth: 220,
    render: (_cell: undefined, row: FocusVersionScheduling) => (
      <Stack direction="row" gap={4}>
        <SchedulingTermDateDisplay label="Term Start" value={row.window_start} />
        <SchedulingTermDateDisplay label="Term End" value={row.window_end} />
      </Stack>
    )
  },
  window_term: {
    label: 'Window term',
    minWidth: 220,
    render: (_cell: undefined, row: FocusVersionScheduling) => (
      <Stack direction="row" gap={4}>
        <SchedulingTermDateDisplay label="Term Start" value={row.window_start} />
        <SchedulingTermDateDisplay label="Term End" value={row.window_end} />
      </Stack>
    )
  },
  audio_languages: {
    label: 'Audio',
    minWidth: 120,
    render: (_: FocusVersionScheduling['audio_languages'], row: FocusVersionScheduling) => <VersionSchedulingAvailChip type="audio" schedule={row} />
  },
  timed_text_languages: {
    label: 'Timed Text',
    minWidth: 120,
    maxWidth: 250,
    render: (_: FocusVersionScheduling['timed_text_languages'], row: FocusVersionScheduling) => (
      <VersionSchedulingAvailChip type="timedtext" schedule={row} />
    )
  },
  territory: {
    label: 'Territories',
    minWidth: 120,
    maxWidth: 250,
    disableSort: true,
    render: (_: FocusVersionScheduling['territory'], row: FocusVersionScheduling) => <VersionSchedulingAvailChip type="territories" schedule={row} />
  },
  status: {
    label: 'Status',
    width: 120,
    minWidth: 120,
    disableSort: true,
    render: (_cell: FocusVersionScheduling['status'], row: FocusVersionScheduling) => <AvailScheduleStatus row={row} />
  },
  overflow: {
    label: 'Actions',
    width: 72,
    isOverflow: true,
    disableSort: true,
    render: (_cell: undefined, row: FocusVersionScheduling) => <AvailScheduleOverflowActions row={row} />
  }
};

const AlphaId = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));
